import React, { useMemo } from 'react';
import AgentTags from 'components/cards/new-layout/components/AgentTags';
import Price from 'components/cards/new-layout/components/Price';
import { getBulletinInfoConfig } from 'components/cards/new-layout/components/info-line-config';
import { useLocale } from 'locale';
import { isNil } from 'lodash';
import { RenewalProgramIndicator } from 'components/real-estate-listing-card/RenewalProgramIndicator';
import { PropertyPrimaryLine, PropertySecondaryLine, YieldRoot } from 'components/cards/new-layout/styled';
import renderLineItem from 'components/labelLineItem';
import { useUniversalCardAddress } from 'components/cards/listing/UniversalCard/utils';
import { getBuildingClassKey } from 'utils/entities';
import { SpacingBlock } from 'components/spacing-block';
import { PropertyInfoText, TagLine } from '../styled';
import { BulletinUniversalCardProps } from 'components/cards/types';
import { UniversalCardExtraProps } from 'components/cards/new-layout/types';
import InfoIcon from 'assets/svg/light-info.svg';
import { TextLabel } from 'ds/components/typography';
import { formatInvestorsYield } from 'utils/investors';
import { State } from 'store/state';
import { routeNameSelector } from 'store/state/selectors/router';
import { connect } from 'react-redux';
import { Route } from 'config/routes';
import { useABTests } from 'hooks/useABTests';
import { URPotential } from 'config/abTests';


const BulletinCard: React.FC<BulletinUniversalCardProps & UniversalCardExtraProps> = (props) => {
  const {
    id,
    isAgent,
    isExclusive,
    madadSearchResult,
    price,
    eventsHistory,
    bedsCount,
    size,
    floor,
    buildingClass,
    streetName,
    streetNumber,
    neighbourhood,
    unitNumber,
    city,
    resolutionPreferences,
    isSearchInWholeCountry,
    investorsData,
    openYieldTagModal,
    routeName,
    urbanRenewalStatus,
  } = props;
  const localeProps = useLocale();
  const { urPotentialMode } = useABTests();
  const { t } = localeProps;
  const isInvestorMarketplace = routeName === Route.InvestorsSearchPage;
  const isURPotential = isInvestorMarketplace && !isNil(urbanRenewalStatus) && urbanRenewalStatus !== -1 && urPotentialMode === URPotential.modeB;

  const infoLineConfig = useMemo(() => {
    return getBulletinInfoConfig(bedsCount, size, floor, buildingClass, localeProps).filter(({ showCondition }) => showCondition);
  }, [ bedsCount, size, buildingClass, localeProps, floor, getBulletinInfoConfig ]);

  const propertyTypeLabel = !!buildingClass ? (
    t('unitPage.buildingClass', { buildingClass: getBuildingClassKey(buildingClass), isCommercial: false })
  ) : null;

  const primaryAddress = useUniversalCardAddress(
    streetName,
    streetNumber,
    neighbourhood,
    unitNumber,
    city,
    resolutionPreferences,
    isSearchInWholeCountry
  );

  const secondaryLine = [ propertyTypeLabel, primaryAddress ].filter(Boolean).join(', ');

  const onYieldClick = React.useCallback((e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (openYieldTagModal) {
      openYieldTagModal(id);
    }
  }, [ openYieldTagModal ]);

  return (
    <>
      <Price price={price} eventsHistory={eventsHistory} direction="down" />
      <TagLine>
        {isInvestorMarketplace && (
          <YieldRoot active={Boolean(investorsData && investorsData.yield)} onClick={onYieldClick} data-auto="yield-info">
            <TextLabel small weight="normal">
              {investorsData && investorsData.yield ? t('widget.search.card.yield', { yield: formatInvestorsYield(investorsData.yield) }) : t('widget.card.yield.empty')}
            </TextLabel>
            <InfoIcon />
          </YieldRoot>
        )}
        {isAgent ? (
          <AgentTags isExclusive={isExclusive} madadSearchResult={madadSearchResult} />
        ) : null}
      </TagLine>

      <SpacingBlock mTop={1}>
        <PropertySecondaryLine data-auto="property-address">
          {secondaryLine}
        </PropertySecondaryLine>
      </SpacingBlock>
      {isURPotential && <RenewalProgramIndicator small status={urbanRenewalStatus} />}
      <PropertyInfoText>
        <PropertyPrimaryLine>
          {infoLineConfig.map(renderLineItem)}
        </PropertyPrimaryLine>
      </PropertyInfoText>
    </>
  );
};


const mapStateToProps = (state: State) => ({
  routeName: routeNameSelector(state),
});

export default connect(mapStateToProps)(BulletinCard);
